import { useQuery } from '@tanstack/react-query';
import { enqueueSnackbar } from '~/utils/notifications';
import { useCallback, useEffect, useRef, useState } from 'react';
import { BackendClient } from '~/backend/backend-client';
import { ContouringClient } from '~/backend/contouring-client';
import { DoseClient } from '~/backend/dose-client';
import {
    createOutageTolerance,
    HttpStatusError,
    POLL_OUTAGE_TOLERANCE_MS,
} from '~/backend/network-retry';
import { PollData, PollingStatus } from '~/backend/types';
import { handleError } from '~/utils/errors';
import { downloadBackendResults } from './utils';

export type ClientParam = ContouringClient | DoseClient;

/**
 * A React hook that implements polling logic to repeatedly query a backend service for results.
 * This hook is used to continuously check the status of a process and retrieve the result once available.
 *
 * @param {BackendClient} client - The client used to perform the polling operation against the backend service.
 * @param {Object} callbacks - Callbacks object for the hook.
 * @param {Function} callbacks.onResult - A callback function that gets executed when the polling operation
 * retrieves a result with a status indicating completion.
 *
 * @returns {Object} An object containing the polling query state and control functions.
 * - @returns {Function} startPolling: A function to start the polling process at a specified interval in milliseconds.
 * - @returns {Function} stopPolling: A function to stop the polling process.
 * - @returns {UseQueryResult<void, Error>} methods from `useQuery` providing access to the query's
 *
 */
export const usePollingQuery = (
    client: BackendClient,
    { onResult }: { onResult: (result: PollData) => void }
) => {
    const [pollInterval, setPollInterval] = useState<number>();
    // The backend keeps processing whatever happens to this tab's
    // network, so a blip must not end the polling and strand the result:
    // transient failures keep the query polling for a bounded outage.
    // `client.poll()` sends a single attempt; the interval is the retry
    // spacing. Fresh for every polling run.
    const outage = useRef(createOutageTolerance(POLL_OUTAGE_TOLERANCE_MS));

    const startPolling = useCallback((interval: number) => {
        outage.current.end();
        outage.current = createOutageTolerance(POLL_OUTAGE_TOLERANCE_MS);
        setPollInterval(interval);
    }, []);

    const stopPolling = useCallback(() => {
        // Stopping mid-outage ends it without a verdict.
        outage.current.end();
        setPollInterval(undefined);
    }, []);

    // Unmounting mid-outage ends it too.
    useEffect(() => () => outage.current.end(), []);
    const query = useQuery({
        queryKey: ['pollingData'],
        queryFn: async () => {
            // Only the request is inside the try: the outage tolerance
            // reads a failure's type, and a defect in the handling below
            // must fail the polling at once, not be polled through.
            let result: PollData;
            try {
                result = await client.poll();
                if (result.status === PollingStatus.POLLING_FAILED) {
                    // An answer with an error status fails the poll like a
                    // thrown error: a temporary server condition counts
                    // towards the outage, and a definitive one ends it.
                    throw new HttpStatusError(
                        result.httpStatus,
                        'Polling for results'
                    );
                }
            } catch (error) {
                if (outage.current.tolerate(error)) {
                    return null;
                }
                stopPolling();
                handleError(error, 'Error when polling the data.');
                return null;
            }
            outage.current.reset();
            if (result.status !== PollingStatus.HAS_RESULT) {
                return null;
            }
            stopPolling();
            try {
                onResult(result);
            } catch (error) {
                // Reported here rather than thrown: a thrown error would be
                // retried, and the result handed over again.
                handleError(error, 'Error when handling the polled data.');
            }
            return result;
        },
        retry: true,
        retryDelay: 5 * 1000,
        refetchInterval: pollInterval,
        enabled: !!pollInterval,
    });

    return { ...query, startPolling, stopPolling };
};

/**
 * A React hook used for processing the results after data has been retrieved:
 * downloads the result files and fetches the backend's error reports through
 * {@link downloadBackendResults}, which leaves a result on the backend unless
 * every download succeeded.
 *
 * @param {BackendClient} client - The client used to download the results and
 * fetch the error reports.
 * @param {Object} callbacks - callback object for the hook.
 * @param {Function} callbacks.onSuccess - Called with the downloaded files when
 * every file was downloaded and the backend reported no error.
 * @param {Function} callbacks.onError - Called with the error messages
 * otherwise; a result whose files did not all arrive is never reported as a
 * success.
 *
 * @returns {Function} handleProcessedResults - Processes the results of a poll.
 */
export function useProcessedResults(
    client: BackendClient,
    {
        onSuccess,
        onError,
    }: {
        onSuccess(files: File[]): void;
        onError(errors: string[]): void;
    }
) {
    const handleProcessedResults = async (data: PollData) => {
        if (!data.result) {
            enqueueSnackbar(`No result data available`, {
                variant: 'error',
            });
            onError(['No result data available']);
            return;
        }

        // The metrics flow has no job id; "metrics" names the job in the
        // download's error messages.
        const { files, errors, downloadErrors } = await downloadBackendResults(
            'metrics',
            client,
            data
        );
        for (const errorMsg of errors) {
            enqueueSnackbar(`Error processing file: ${errorMsg}`, {
                variant: 'error',
            });
        }
        if (errors.length > 0 || downloadErrors.length > 0) {
            onError([...errors, ...downloadErrors]);
        } else {
            onSuccess(files);
        }
    };

    return handleProcessedResults;
}
